<template>
    <div v-if="isDisplayed" class="receipt-overview">
        <div v-if="loading" class="receipt-overview_loader / flex">
            <VJoySpinner />
        </div>
        <div class="receipt-overview_document / flex">
            <div class="receipt-overview_document_header / flex">
                <div class="receipt-overview_document_header_extremity"></div>
                <div class="receipt-overview_document_header_name ellipsis">{{ receiptName }}</div>
                <div class="receipt-overview_document_header_extremity">
                    <VJoyIconButton size="small" icon="cross" variant="ghost" @click="emit('closeOverview')" />
                </div>
            </div>
            <VJoyWrapper v-if="error" row-gap="2" direction="column" align="center" justify="center" style="height: 100%">
                <MaltIllustration name="plug-unplugged--high" width="200" alt="" />
                <VJoyText preset="primary-650">{{ $t('projectclient.expenses.list.receipt.overview.error.title') }}</VJoyText>
                <VJoyText preset="primary-400">{{ $t('projectclient.expenses.list.receipt.overview.error.subtitle') }}</VJoyText>
            </VJoyWrapper>
            <iframe
                v-else-if="transformedUrl && fileType === 'pdf'"
                class="receipt-overview_document_iframe"
                type="application/pdf"
                :src="transformedUrl"
            ></iframe>
            <img v-else-if="fileType === 'image'" :src="fileUrl" class="receipt-overview_document_imagge" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {fetch} from 'ofetch';
    import {MaltIllustration} from '#components';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyIconButton, VJoySpinner, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';

    const props = defineProps<{
        drawerWidth: number;
        receiptName: string;
        fileUrl?: string;
    }>();

    const emit = defineEmits<{
        closeOverview: [];
    }>();

    const {windowWidth} = useMediaQuery();

    const fileType = computed<'pdf' | 'image'>(() => {
        if (props.receiptName.endsWith('.pdf')) {
            return 'pdf';
        }
        return 'image';
    });

    const transformedUrl = ref();

    const loading = ref(true);
    const error = ref(false);

    onMounted(async () => {
        // PDF is returned with `octet-stream` Content-type, to avoid it being downloaded automatically,
        // we read it as a stream then convert it to base64, and display it
        // Please insult Backends for this, not frontends.
        try {
            if (props.fileUrl && fileType.value === 'pdf') {
                const response = await fetch(props.fileUrl);
                if (response.status === 200) {
                    const reader = response.body?.getReader();

                    if (reader) {
                        const stream = await new ReadableStream({
                            start(controller) {
                                return pump();
                                function pump(): any {
                                    return reader?.read().then(({done, value}) => {
                                        if (done) {
                                            controller.close();
                                            return;
                                        }
                                        controller.enqueue(value);
                                        return pump();
                                    });
                                }
                            },
                        });
                        const response = await new Response(stream);
                        const blob = await response.blob();
                        const pdfBlob = blob.slice(0, blob.size, 'application/pdf');

                        const fileUrlFromBlob = URL.createObjectURL(pdfBlob);

                        if (typeof fileUrlFromBlob === 'string') {
                            transformedUrl.value = fileUrlFromBlob;
                        }
                    }
                } else {
                    error.value = true;
                }
            }
        } finally {
            loading.value = false;
        }
    });

    const viewerWidth = computed(() => {
        return `${windowWidth.value - props.drawerWidth}px`;
    });

    const isDisplayed = computed(() => {
        return windowWidth.value - props.drawerWidth > 300;
    });
</script>

<style lang="scss" scoped>
    .receipt-overview {
        position: absolute;
        z-index: 10;
        padding: var(--joy-core-spacing-6);
        width: v-bind(viewerWidth);
        height: 100%;

        &_loader {
            height: 100%;
            width: 100%;
            background-color: var(--joy-color-background-default);
            border-radius: var(--joy-core-radius-3);
            justify-content: center;
            align-items: center;
        }

        &_document {
            display: flex;
            flex-flow: column nowrap;
            height: 100%;
            width: 100%;
            background-color: var(--joy-color-background-default);
            border-radius: var(--joy-core-radius-3);
            overflow: hidden;

            &_header {
                display: flex;
                flex-flow: row nowrap;
                flex: 0 0 auto;
                height: 50px;
                align-items: center;
                border-bottom: 1px solid var(--joy-color-neutral-30);

                &_extremity {
                    width: 50px;
                    flex: 0 0 auto;
                    display: flex;
                    justify-content: center;
                }

                &_name {
                    text-align: center;
                    padding: 0 var(--joy-core-spacing-2);
                    font-weight: var(--joy-font-weight-bold);
                }
            }

            &_iframe {
                height: 100%;
                width: 100%;
                background-color: var(--joy-color-background-default);
                padding: var(--joy-core-spacing-4);
                border: none;
            }
        }
    }
</style>
